import { createColumnHelper } from '@tanstack/react-table';
import { useFormContext } from 'react-hook-form';
import { PortfolioEnterpriseRelationshipResult } from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { ColumnFilterType, Icon, IconName, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { UncontrolledNumberInputField } from '@mews-commander/form';
import {
    AdjustmentType,
    getServicesPropertiesFieldName,
    ServicesFieldNames,
} from '../../../../forms/schemas/configureProductFormSchema';
import { PropertyNameCell } from '../../../common/PropertyNameCell';
import { PercentageAdjustmentSelect } from './PercentageAdjustmentSelect';

interface Props {
    percentageValueFieldName: string;
    percentageApplicationFieldName: string;
}
const HelperIcon = ({ percentageValueFieldName, percentageApplicationFieldName }: Props) => {
    const { getFieldState } = useFormContext();

    const { invalid: percentageInvalid, isDirty: isPercentageDirty } = getFieldState(percentageValueFieldName) || {};
    const { invalid: applicationInvalid, isDirty: isApplicationDirty } =
        getFieldState(percentageApplicationFieldName) || {};

    const isSelectedValue = !percentageInvalid && isPercentageDirty && !applicationInvalid && isApplicationDirty;

    return (
        <Icon
            size="small"
            value={isSelectedValue ? IconName.CheckCircle : IconName.AlertCircle}
            variant={isSelectedValue ? 'success' : 'danger'}
        />
    );
};
const columnHelper = createColumnHelper<PortfolioEnterpriseRelationshipResult & { defaultCurrencyCode: string }>();

export const getPercentagePricingColumns = (selectedCountries: string[], currentService: string) => [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => {
            const name = props.getValue();
            const { id, defaultCurrencyCode } = props.row.original;
            return <PropertyNameCell id={id} name={name} defaultCurrencyCode={defaultCurrencyCode} />;
        },
    }),
    columnHelper.accessor('countryName', {
        header: () => lt('Country'),
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.MultiSelect,
            getMultiSelectOptions: () =>
                selectedCountries.map((value) => ({
                    value,
                    label: value,
                })),
        },
    }),
    columnHelper.accessor('id', {
        meta: {
            width: 220,
        },
        header: () => lt('GeneralPercentage'),
        cell: (props) => {
            const id = props.getValue();
            // TODO fix name
            return (
                <UncontrolledNumberInputField
                    name={`${getServicesPropertiesFieldName(
                        currentService,
                        AdjustmentType.PercentagePriceAdjustments,
                        id
                    )}.${ServicesFieldNames.Multiplier}`}
                    inline
                    suffix={UnicodeEntity.Percent}
                    type="number"
                />
            );
        },
    }),
    columnHelper.display({
        id: 'servicePercentageApplication',
        meta: {
            width: 220,
        },
        header: () => lt('ConfigureProductServicesFieldApplyTo'),
        cell: (props) => {
            const { id } = props.row.original;
            return (
                <Stack vertical alignment="center" fill>
                    <PercentageAdjustmentSelect
                        name={`${getServicesPropertiesFieldName(
                            currentService,
                            AdjustmentType.PercentagePriceAdjustments,
                            id
                        )}.${ServicesFieldNames.AmountPart}`}
                        inline
                    />
                </Stack>
            );
        },
    }),
    columnHelper.display({
        id: 'icon',
        cell: (props) => {
            const { id } = props.row.original;

            const currentProperty = `${getServicesPropertiesFieldName(
                currentService,
                AdjustmentType.PercentagePriceAdjustments,
                id
            )}`;

            return (
                <HelperIcon
                    percentageApplicationFieldName={`${currentProperty}.${ServicesFieldNames.AmountPart}`}
                    percentageValueFieldName={`${currentProperty}.${ServicesFieldNames.Multiplier}`}
                />
            );
        },
    }),
];
